import React, { useEffect, useMemo, useState } from 'react'

import type {
    GetModuleTimelineOutput,
    ModuleVersionGroup,
    TimelineEvent,
} from '@amzn/aa-workbench-notification-service-client'
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar'
import { Breadcrumbs } from '@amzn/stencil-react-components/breadcrumbs'
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { Chip } from '@amzn/stencil-react-components/chip'
import { Expander, ExpanderContent } from '@amzn/stencil-react-components/expander'
import {
    FilterBar,
    FilterController,
    FilterList,
    FilterSelections,
} from '@amzn/stencil-react-components/filtering'
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner'
import {
    StatusIndicatorIcon,
    StatusIndicatorSize,
} from '@amzn/stencil-react-components/status-indicator'
import { H2, H4, Text } from '@amzn/stencil-react-components/text'
import { IconChevronDownSmall, IconChevronRightSmall } from '@amzn/stencil-react-icons'

import { NotificationServiceClient } from 'src/services/notificationServiceClient'
import {
    filterStageEvents,
    formatDurationMs,
    getEventLabel,
    getStageStatus,
    getStatusLabel,
    getTimelineErrorMessage,
    getVersionChip,
    groupConsecutiveDraftSaves,
    parseEventPayload,
} from './moduleTimelineHelpers'

function formatDate(date: Date | undefined, dateOnly = false): string {
    if (!date) return ''
    return dateOnly
        ? date.toLocaleDateString()
        : `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
          })}`
}

const EventFeedItem: React.FC<{ event: TimelineEvent }> = ({ event }) => {
    const baseLabel = getEventLabel(event.eventType ?? '')
    const { comment, revisionNumber, previousStatus, newStatus } = parseEventPayload(event)
    const REVIEW_EVENT_TYPES = new Set([
        'MODULE_APPROVAL_PROCESS_STARTED',
        'MODULE_REVIEW_APPROVED',
        'MODULE_REVIEW_APPROVED_ASYNC',
        'MODULE_REVIEW_REVOKED',
        'MODULE_REVIEW_REVOKED_ASYNC',
    ])
    const label =
        revisionNumber && REVIEW_EVENT_TYPES.has(event.eventType ?? '')
            ? `${baseLabel} (revision ${revisionNumber})`
            : baseLabel
    const transition = newStatus
        ? previousStatus
            ? `${getStatusLabel(previousStatus)} → ${getStatusLabel(newStatus)}`
            : `→ ${getStatusLabel(newStatus)}`
        : undefined

    return (
        <Row
            alignItems='flex-start'
            dataTestId={`module-audit-event-${event.eventId}`}
            gridGap='S300'
            padding={{ top: 'S300', bottom: 'S300' }}
        >
            <Avatar
                showImage={true}
                showUsername={true}
                size={AvatarSize.Small}
                username={event.actorUserId ?? ''}
            />
            <Col gridGap='S100' style={{ flex: 1, paddingTop: 4 }}>
                <Row alignItems='baseline' gridGap='S200'>
                    <Text color='neutral90' fontSize='T200'>
                        {label}
                    </Text>
                    {transition && (
                        <Text
                            color='neutral70'
                            dataTestId='module-audit-event-transition'
                            fontSize='T200'
                        >
                            {transition}
                        </Text>
                    )}
                </Row>
                {Boolean(comment) && (
                    <View backgroundColor='neutral05' borderRadius='S200' padding='S200'>
                        <Text color='neutral70' fontSize='T200' style={{ whiteSpace: 'pre-wrap' }}>
                            {comment}
                        </Text>
                    </View>
                )}
            </Col>
            <Text
                color='neutral70'
                fontSize='T100'
                style={{ whiteSpace: 'nowrap', textAlign: 'right', flexShrink: 0, paddingTop: 4 }}
            >
                {formatDate(event.timestamp)}
            </Text>
        </Row>
    )
}

const CollapsedDraftSaves: React.FC<{ events: TimelineEvent[] }> = ({ events }) => {
    const [expanded, setExpanded] = useState(false)
    const actor = events[0].actorUserId ?? ''
    const firstTimestamp = events[0].timestamp
    const lastTimestamp = events[events.length - 1].timestamp

    return (
        <Col>
            <Row
                alignItems='center'
                aria-expanded={expanded}
                dataTestId='module-audit-draft-group-header'
                gridGap='S300'
                padding={{ top: 'S300', bottom: 'S300' }}
                role='button'
                style={{ cursor: 'pointer' }}
                tabIndex={0}
                onClick={() => setExpanded(!expanded)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setExpanded(!expanded)
                    }
                }}
            >
                <Avatar
                    showImage={true}
                    showUsername={true}
                    size={AvatarSize.Small}
                    username={actor}
                />
                <Col gridGap='S100' style={{ flex: 1 }}>
                    <Text color='neutral90' fontSize='T200'>
                        {events.length === 1 ? '1 draft save' : `${events.length} draft saves`}
                    </Text>
                </Col>
                <Row alignItems='center' gridGap='S300' style={{ flexShrink: 0 }}>
                    <Text color='neutral70' fontSize='T100' style={{ whiteSpace: 'nowrap' }}>
                        {events.length === 1
                            ? formatDate(firstTimestamp)
                            : `${formatDate(firstTimestamp)} – ${formatDate(lastTimestamp)}`}
                    </Text>
                    {expanded ? (
                        <IconChevronDownSmall aria-hidden='true' />
                    ) : (
                        <IconChevronRightSmall aria-hidden='true' />
                    )}
                </Row>
            </Row>
            <ExpanderContent
                dataTestId='module-audit-draft-group-content'
                isExpanded={expanded}
                padding={{ left: 'S500' }}
            >
                {events.map((event) => (
                    <EventFeedItem key={event.eventId} event={event} />
                ))}
            </ExpanderContent>
        </Col>
    )
}

const ModuleVersionCard: React.FC<{
    vg: ModuleVersionGroup
    index: number
    expandByDefault?: boolean
    filterUsers: string[]
    filterEventTypes: string[]
}> = ({ vg, index, expandByDefault, filterUsers, filterEventTypes }) => {
    const { label: versionStatus, color: chipColor } = getVersionChip(vg.status)
    // A version is "closed" when the backend set a dateTo (it reached a terminal status OR was
    // superseded by a newer version). Only a version with no dateTo is still open-ended, so drive
    // the open/active/ongoing display off dateTo rather than the coarse rollup status — a superseded
    // version can still be rolled up `in_progress` yet must not render as "present/ongoing".
    const isOpenEnded = vg.dateTo == null
    const stages = useMemo(() => vg.stages ?? [], [vg.stages])

    const filteredStages = useMemo(
        () => filterStageEvents(stages, filterUsers, filterEventTypes),
        [stages, filterUsers, filterEventTypes]
    )

    if (filteredStages.length === 0) return null

    const dateRange = isOpenEnded
        ? `${formatDate(vg.dateFrom, true)} – present`
        : `${formatDate(vg.dateFrom, true)} – ${formatDate(vg.dateTo ?? vg.dateFrom, true)}`

    const versionLabel = `Version ${index + 1}`

    return (
        <Card flexDirection='column' padding='S400'>
            <Expander
                dataTestId={`module-audit-version-${vg.moduleVersionId}`}
                headingLevel={3}
                renderTitle={(titleProps) => (
                    <Row {...titleProps} alignItems='center' gridGap='S300' width='100%'>
                        <Text fontSize='T300' fontWeight='bold'>
                            {versionLabel}
                        </Text>
                        <Chip
                            backgroundColor={chipColor}
                            dataTestId={`module-audit-status-${vg.moduleVersionId}`}
                        >
                            {versionStatus}
                        </Chip>
                        <Spacer flex='1' />
                        <Text
                            color='neutral70'
                            fontSize='T100'
                            style={{ minWidth: 130, textAlign: 'right', marginRight: 36 }}
                        >
                            {dateRange}
                        </Text>
                    </Row>
                )}
                shouldExpandOnMount={expandByDefault}
                titleText={versionLabel}
            >
                <Col gridGap='S500' padding={{ top: 'S400' }} width='100%'>
                    {filteredStages.map((stage, stageIndex) => {
                        const isLast = stageIndex === filteredStages.length - 1
                        // Only the last stage of a still-open version is active; everything else is done.
                        const isActive = isOpenEnded && isLast
                        const isDone = !isActive
                        const wasReverted = (stage.revocationCount ?? 0) > 0

                        return (
                            <Col
                                key={`${stage.stageName ?? ''}-${stageIndex}`}
                                dataTestId={`module-audit-stage-${stage.stageName ?? ''}`}
                                padding={{ bottom: 'S200', left: 'S200' }}
                            >
                                <Row
                                    alignItems='center'
                                    gridGap='S200'
                                    padding={{ bottom: 'S200' }}
                                >
                                    <StatusIndicatorIcon
                                        iconAriaHidden={true}
                                        size={StatusIndicatorSize.Large}
                                        status={getStageStatus(isDone, isActive, wasReverted)}
                                    />
                                    <H4 color='neutral90' fontSize='T300' fontWeight='regular'>
                                        {stage.stageName}
                                    </H4>
                                    <Text
                                        color='neutral70'
                                        dataTestId='module-audit-stage-duration'
                                        fontSize='T100'
                                    >
                                        {formatDurationMs(stage.durationMs)}
                                        {wasReverted ? ` · ${stage.revocationCount} revoked` : ''}
                                    </Text>
                                </Row>
                                <Col style={{ paddingLeft: 4 }}>
                                    {groupConsecutiveDraftSaves(stage.events ?? []).map((group) => {
                                        if (group[0].eventType === 'MODULE_DRAFT_SAVED') {
                                            return (
                                                <CollapsedDraftSaves
                                                    key={group[0].eventId}
                                                    events={group}
                                                />
                                            )
                                        }
                                        return (
                                            <EventFeedItem
                                                key={group[0].eventId}
                                                event={group[0]}
                                            />
                                        )
                                    })}
                                </Col>
                            </Col>
                        )
                    })}
                </Col>
            </Expander>
        </Card>
    )
}

export interface ModuleTimelineTabProps {
    moduleId: string
    /**
     * When provided, the audit trail renders a breadcrumb back-link ("Module" → "Audit trail")
     * whose click calls onClose. Used by the inline view-swap on the module viewer page so the
     * trail reads as its own page rather than a cramped modal. Omit it to render the bare trail.
     */
    onClose?: () => void
}

const TimelineShell: React.FC<{ onClose?: () => void; children: React.ReactNode }> = ({
    onClose,
    children,
}) => {
    if (!onClose) return <>{children}</>
    return (
        <Col gridGap='S400' padding={{ bottom: 'S500' }} width='100%'>
            <Row padding={{ bottom: 'S100' }}>
                <Breadcrumbs
                    crumbs={[{ title: 'Module', url: '#' }, { title: 'Audit trail' }]}
                    onCrumbClick={(e) => {
                        e.preventDefault()
                        onClose()
                    }}
                />
            </Row>
            {children}
        </Col>
    )
}

export const ModuleTimelineTab = ({ moduleId, onClose }: ModuleTimelineTabProps) => {
    const [sortAsc, setSortAsc] = useState(false)
    const [filterSelections, setFilterSelections] = useState<FilterSelections>({})
    const [timelineData, setTimelineData] = useState<GetModuleTimelineOutput | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        const fetchTimeline = async () => {
            setIsLoading(true)
            setError(null)
            try {
                const data = await NotificationServiceClient.getModuleTimeline(moduleId)
                if (!cancelled) setTimelineData(data)
            } catch (err) {
                if (!cancelled) setError(getTimelineErrorMessage(err))
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }
        if (moduleId) {
            void fetchTimeline()
        } else {
            setIsLoading(false)
        }
        return () => {
            cancelled = true
        }
    }, [moduleId])

    const versions = useMemo(() => timelineData?.versions ?? [], [timelineData])

    const { userOptions, eventTypeOptions } = useMemo(() => {
        const userCounts = new Map<string, number>()
        const typeCounts = new Map<string, number>()
        versions.forEach((v) => {
            ;(v.stages ?? []).forEach((s) => {
                ;(s.events ?? []).forEach((e) => {
                    const user = e.actorUserId ?? ''
                    const type = e.eventType ?? ''
                    if (user) userCounts.set(user, (userCounts.get(user) ?? 0) + 1)
                    if (type) typeCounts.set(type, (typeCounts.get(type) ?? 0) + 1)
                })
            })
        })
        return {
            userOptions: Array.from(userCounts.entries()).map(([u, count]) => ({
                label: u,
                value: u,
                count,
            })),
            eventTypeOptions: Array.from(typeCounts.entries()).map(([t, count]) => ({
                label: getEventLabel(t),
                value: t,
                count,
            })),
        }
    }, [versions])

    const filterUsers = (filterSelections.user as string[] | undefined) ?? []
    const filterEventTypes = (filterSelections.eventType as string[] | undefined) ?? []

    // versions arrive oldest-first from the service; index them before any display reversal so the
    // "Version N" label stays stable regardless of sort direction.
    const indexedVersions = useMemo(() => versions.map((vg, index) => ({ vg, index })), [versions])
    const displayedVersions = useMemo(
        () => (sortAsc ? indexedVersions : [...indexedVersions].reverse()),
        [indexedVersions, sortAsc]
    )

    if (isLoading) {
        return (
            <TimelineShell onClose={onClose}>
                <Row justifyContent='center' padding={{ top: 'S500' }}>
                    <Spinner dataTestId='module-audit-loading' size={SpinnerSize.Medium} />
                </Row>
            </TimelineShell>
        )
    }

    if (error) {
        return (
            <TimelineShell onClose={onClose}>
                <Text color='error70' dataTestId='module-audit-error'>
                    {error}
                </Text>
            </TimelineShell>
        )
    }

    if (versions.length === 0) {
        return (
            <TimelineShell onClose={onClose}>
                <Col gridGap='S300'>
                    <H2>{'Module audit trail'}</H2>
                    <Text dataTestId='module-audit-empty'>
                        {'No audit trail data exists for this module yet.'}
                    </Text>
                </Col>
            </TimelineShell>
        )
    }

    return (
        <TimelineShell onClose={onClose}>
            <Col gridGap='S400' padding={{ bottom: 'S500' }} width='100%'>
                <MessageBanner
                    dataTestId='module-audit-banner'
                    isDismissible={false}
                    type={MessageBannerType.Informational}
                >
                    {`Events for this module prior to ${formatDate(
                        versions[0].dateFrom
                    )} may not be available.`}
                </MessageBanner>

                <H2>{'Module audit trail'}</H2>

                <Row alignItems='center' gridGap='S300'>
                    <FilterBar
                        flyoutTitleText='All filters'
                        resetButtonText='Clear all filters'
                        shouldShowAppliedFiltersRow={false}
                        onApply={setFilterSelections}
                    >
                        <FilterController name='eventType' titleText='Event type'>
                            <FilterList
                                applyButtonText='Apply'
                                isMulti={true}
                                options={eventTypeOptions}
                                resetButtonText='Clear filters'
                                titleText='Event name'
                                value={filterSelections.eventType}
                                onFilterChange={(value) =>
                                    setFilterSelections((prev) => ({ ...prev, eventType: value }))
                                }
                            />
                        </FilterController>
                        <FilterController name='user' titleText='User'>
                            <FilterList
                                applyButtonText='Apply'
                                isMulti={true}
                                options={userOptions}
                                resetButtonText='Clear filters'
                                titleText='User'
                                value={filterSelections.user}
                                onFilterChange={(value) =>
                                    setFilterSelections((prev) => ({ ...prev, user: value }))
                                }
                            />
                        </FilterController>
                    </FilterBar>
                    <Spacer flex='1' />
                    <Button
                        dataTestId='module-audit-sort-button'
                        variant={ButtonVariant.Tertiary}
                        onClick={() => setSortAsc(!sortAsc)}
                    >
                        {sortAsc ? 'Oldest version first ↑' : 'Newest version first ↓'}
                    </Button>
                </Row>

                <Col dataTestId='module-audit-versions' gridGap='S400'>
                    {displayedVersions.map(({ vg, index }, position) => (
                        <ModuleVersionCard
                            key={vg.moduleVersionId}
                            expandByDefault={position === 0}
                            filterEventTypes={filterEventTypes}
                            filterUsers={filterUsers}
                            index={index}
                            vg={vg}
                        />
                    ))}
                </Col>
            </Col>
        </TimelineShell>
    )
}
